// calendar month without header(sun~sat)

import React, { Ref, RefObject, useRef } from 'react';

import { css, cx } from '@emotion/css';
import { useInView } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useMediaQuery } from 'react-responsive';

import { RentalCarOfficeLocationType } from '^/AppContext/RentalOfficeLocationRepo';
import { CalendarStateViewModel } from '^/components/Calendar/BasicCalendar/Calendar.state.viewmodel';
import { CalendarMonthViewModel } from '^/components/Calendar/BasicCalendar/components/Month/Calendar.Month.viewmdel';
import { SPACING } from '^/constants/commonStyles';
import useWindowResize from '^/hooks/useWindowResize';
import { MobxInputValue } from '^/util/MobxInputValue';

import { isYearMonthInsideSelectionRange } from '../../../BasicCalendar/helpers/isYearMonthInsideSelectionRange';
import { CalendarHeader } from '../Calendar.header';
import { CalendarDay } from '../Day/Calendar.Day';
import { WeekDays } from '../WeekDays';

const classNames = {
  daysWrapper: css`
    display: flex;
    flex-flow: wrap;
    width: 100%;
    margin-top: 8px;
  `,
};

export const CalendarMonth: React.FC<{
  isCalendarWidthTimeMaxSize?: boolean,
  selectionEndRef: Ref<HTMLDivElement>,
  containerRef?: RefObject<Element>,
  hasDifferentPickupReturnLocations: MobxInputValue<boolean>,
  pickupLocation: RentalCarOfficeLocationType,
  returnLocation: RentalCarOfficeLocationType,
  viewmodel: CalendarMonthViewModel,
  showPrevButton?: boolean,
  showNextButton?: boolean,
  stateViewModel: CalendarStateViewModel,
}> = observer(function CalendarMonth({
  isCalendarWidthTimeMaxSize,
  selectionEndRef,
  containerRef,
  hasDifferentPickupReturnLocations,
  pickupLocation,
  returnLocation,
  viewmodel,
  showPrevButton,
  showNextButton,
  stateViewModel,
}) {
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const [width] = useWindowResize();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    root: containerRef,
    margin: `${width * 2}px 0px`,
  });
  const isInSelectionRange = isYearMonthInsideSelectionRange(
    viewmodel.yearMonth,
    stateViewModel.selection,
  );

  const selectionEnd = stateViewModel.selection.end;

  const hasSelectionEndDate = isInSelectionRange
    && selectionEnd
    && selectionEnd.year === viewmodel.yearMonth.year
    && selectionEnd.yearMonth.monthFrom0 === viewmodel.yearMonth.monthFrom0;

  return (
    <div
      ref={ref}
      className={cx(
        isMobile
          ? css`
            min-height: 80vw;
          `
          : css`
              width: 100%;
              border: solid 1px #a9d3f9;
              border-radius: 10px;
              padding: ${SPACING.spacing24};
            `,
      )}
    >

      <CalendarHeader
        calendarStateVM={stateViewModel}
        yearMonth={viewmodel.yearMonth}
        showNextButton={showNextButton}
        showPrevButton={showPrevButton}
      />

      <WeekDays />
      {(inView || null) && (
        <div
          className={cx(
            classNames.daysWrapper,
          )}
        >
          {[...Array(viewmodel.startWeekDay - 1 > -1 ? viewmodel.startWeekDay - 1 : 6)].map((_, i) => (
            <div
              key={i}
              className={
                css`
                  width: 14.28%;
                `
              }
            >
            </div>
          ))}
          {viewmodel
            .dayViewModels
            .map((vm) => (
              <CalendarDay
                monthInSelectionRange={isInSelectionRange}
                hasDifferentPickupReturnLocations={
                  hasDifferentPickupReturnLocations
                }
                pickupLocation={pickupLocation}
                returnLocation={returnLocation}
                stateViewModel={stateViewModel}
                key={vm.str}
                viewmodel={vm}
                elemRef={(
                  (
                    hasSelectionEndDate
                    && vm.day === selectionEnd.day
                  ) || undefined)
                  && selectionEndRef}
              />
            ),
            )}
        </div>
      )}
    </div>

  );
});
